import React, { useState, useRef, useEffect } from 'react';

type TooltipTheme = 'light' | 'dark';

const TOOLTIP_THEMES: Record<TooltipTheme, { backgroundColor: string; titleColor: string; textColor: string }> = {
    light: {
        backgroundColor: 'var(--ds-tooltip-surface)',
        titleColor: 'var(--ds-tooltip-ink)',
        textColor: 'var(--ds-ink-secondary)',
    },
    dark: {
        backgroundColor: 'var(--ds-surface-inverse)',
        titleColor: 'var(--ds-ink-inverse)',
        textColor: 'var(--ds-ink-inverse)',
    },
};

type TooltipProps = {
    text?: string | React.ReactNode;
    content?: any;
    children: React.ReactNode;
    title?: string;
    theme?: TooltipTheme;
}

const Tooltip = ({ text, content, children, title, theme = "dark" }: TooltipProps) => {
    const themeStyles = theme ? TOOLTIP_THEMES[theme] : undefined;
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isVisible && triggerRef.current && tooltipRef.current) {
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const tooltipWidth = tooltipRef.current.offsetWidth;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let newTop = triggerRect.bottom + window.scrollY;
            let newLeft = triggerRect.left + (triggerRect.width - tooltipWidth) / 2 + window.scrollX;

            if (newLeft + tooltipWidth > viewportWidth) {
                newLeft = viewportWidth - tooltipWidth - 10;
            } else if (newLeft < 0) {
                newLeft = 10;
            }

            if (newTop + tooltipRef.current.offsetHeight > viewportHeight) {
                newTop = triggerRect.top + window.scrollY - tooltipRef.current.offsetHeight;
            }

            setPosition((prevPosition) => {
                if (prevPosition.top !== newTop || prevPosition.left !== newLeft) {
                    return { top: newTop, left: newLeft };
                }
                return prevPosition;
            });
        }
    }, [isVisible]);

    // ✅ Determine if text is a string for styling purposes
    const isTextString = typeof text === 'string';

    return (
        <>
            <div
                ref={triggerRef}
                onMouseEnter={() => setIsVisible(true)}
                onMouseLeave={() => setIsVisible(false)}
                className="tw-font-semibold tw-text-xl tw-text-ink tw-truncate tw-block"
            >
                {children}
            </div>
            {isVisible && (
                <div
                    ref={tooltipRef}
                    className={content != null ? "tw-fixed tw-z-50 tw-p-2" : "tw-fixed tw-z-50 tw-p-2 tw-bg-surface-inverse tw-text-ink-inverse tw-text-sm tw-rounded-md tw-shadow-lg"}
                    style={content != null ? {
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                    } : {
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                        maxWidth: theme === 'light' ? '234px' : '600px',
                        whiteSpace: isTextString ? 'pre-line' : 'normal',
                        wordWrap: 'break-word',
                        ...(themeStyles ? { backgroundColor: themeStyles.backgroundColor } : {})
                    }}
                >
                    {content ?? (
                        <div>
                            <div className="tw-text-sm tw-mb-1" style={themeStyles ? { color: themeStyles.titleColor } : {}}>{title}</div>
                            <div className="tw-font-normal tw-text-xs tw-opacity-80" style={themeStyles ? { color: themeStyles.textColor } : {}}>{text}</div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default Tooltip;